import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import { translations, worksData, type Language, type Translations } from './translations';

export default function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

function AppContent() {
  const [lang, setLang] = useState<Language>(() => {
    // Initialize language from localStorage or default to 'en'
    const savedLang = localStorage.getItem('amem-lang');
    return (savedLang === 'ar' || savedLang === 'en') ? savedLang : 'en';
  });

  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    // Initialize theme from localStorage or system preference
    const savedTheme = localStorage.getItem('amem-theme');
    if (savedTheme === 'light' || savedTheme === 'dark') {
      return savedTheme;
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  useEffect(() => {
    // Set theme on mount
    document.body.setAttribute('data-theme', theme);
  }, []);

  useEffect(() => {
    // Update language attribute and save to localStorage
    document.body.setAttribute('lang', lang);
    localStorage.setItem('amem-lang', lang);
  }, [lang]);

  useEffect(() => {
    // Save theme to localStorage when it changes
    localStorage.setItem('amem-theme', theme);
  }, [theme]);

  const t = translations[lang] as Translations;

  const toggleLang = () => {
    setLang(lang === 'en' ? 'ar' : 'en');
  };

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    document.body.setAttribute('data-theme', newTheme);
  };

  const location = useLocation();

  return (
    <>
      <Header
        lang={lang}
        theme={theme}
        t={t}
        currentPath={location.pathname}
        onToggleLang={toggleLang}
        onToggleTheme={toggleTheme}
      />
      <main>
        <div className="container">
          <Routes>
            <Route path="/" element={<HomePage t={t} lang={lang} />} />
            <Route path="/cv" element={<CVPage t={t} />} />
            <Route path="/works" element={<WorksPage t={t} lang={lang} />} />
          </Routes>
        </div>
      </main>
      <Footer t={t} />
    </>
  );
}

function Header({
  lang,
  theme,
  t,
  currentPath,
  onToggleLang,
  onToggleTheme
}: {
  lang: Language;
  theme: string;
  t: Translations;
  currentPath: string;
  onToggleLang: () => void;
  onToggleTheme: () => void;
}) {
  return (
    <header>
      <div className="container">
        <div className="header-content">
          <div className="logo-section">
            <img src="/assets/icon.png" alt="Amem Company" className="logo" />
            <div className="company-name">{t.companyName}</div>
          </div>

          <nav>
            <Link to="/" className={currentPath === '/' ? 'active' : ''}>
              {t.home}
            </Link>
            <Link to="/cv" className={currentPath === '/cv' ? 'active' : ''}>
              {t.cv}
            </Link>
            <Link to="/works" className={currentPath === '/works' ? 'active' : ''}>
              {t.works}
            </Link>
          </nav>

          <div className="controls">
            <button onClick={onToggleLang} className="btn">
              {lang === 'en' ? 'العربية' : 'English'}
            </button>
            <button onClick={onToggleTheme} className="btn">
              {theme === 'light' ? '🌙' : '☀️'}
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}

function HomePage({ t, lang }: { t: Translations; lang: Language }) {
  return (
    <>
      <section className="hero">
        <h1>{t.companyName}</h1>
        <p>{t.aboutText}</p>
      </section>

      <div className="cards">
        <div className="card">
          <h3>{t.specialty}</h3>
          <ul>
            <li>{t.specialties.construction}</li>
            <li>{t.specialties.electric}</li>
            <li>{t.specialties.mechanical}</li>
          </ul>
        </div>

        <div className="card">
          <h3>{t.contact}</h3>
          <div className="info-item">
            <div className="info-label">{t.phone}</div>
            <div className="info-value">{t.phoneNumber}</div>
          </div>
          <div className="info-item" style={{ marginTop: '1rem' }}>
            <div className="info-label">{t.location}</div>
            <div className="info-value">{t.locationText}</div>
          </div>
          <div className="info-item" style={{ marginTop: '1rem' }}>
            <div className="info-label">{t.director}</div>
            <div className="info-value">{t.directorName}</div>
          </div>
        </div>

        <div className="card">
          <h3>{t.founded}</h3>
          <p style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--accent)' }}>
            {t.foundedYear}
          </p>
          <div style={{ marginTop: '1rem' }}>
            <div className="info-label">{t.capital}</div>
            <div className="info-value">{t.capitalAmount}</div>
          </div>
        </div>
      </div>

      <div style={{ marginTop: '3rem' }}>
        <div className="card">
          <h3>{t.assets}</h3>
          <ul>
            {t.assetsList.map((asset, index) => (
              <li key={index}>{asset}</li>
            ))}
          </ul>
        </div>
      </div>
    </>
  );
}

function CVPage({ t }: { t: Translations }) {
  return (
    <div className="cv-container">
      <h1>{t.downloadCV}</h1>
      <p>{t.cvDescription}</p>
      <a href="/api/cv/download" download className="btn btn-primary download-btn">
        📄 {t.downloadCV}
      </a>
    </div>
  );
}

function WorksPage({ t, lang }: { t: Translations; lang: Language }) {
  const years = Object.keys(worksData).sort((a, b) => Number(b) - Number(a));

  return (
    <>
      <div className="hero">
        <h1>{t.ourWorks}</h1>
      </div>

      <div className="timeline">
        {years.map((year) => {
          const projects = worksData[year as keyof typeof worksData][lang];

          return (
            <div key={year} className="timeline-year">
              <h3>{year}</h3>
              <div className="timeline-projects">
                {projects.map((project, index) => (
                  <div key={index} className="project-item">
                    <div className="project-name">{project.name}</div>
                    <div className="project-cost">
                      {t.cost}: {project.cost} {project.currency === 'dinars' ? t.dinars : t.dollars}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

function Footer({ t }: { t: Translations }) {
  return (
    <footer>
      <div className="container">
        <div className="footer-content">
          <p>{t.copyright}</p>
        </div>
      </div>
    </footer>
  );
}
